<template>
  <div class="donut-chart-card">
    <!-- Header -->
    <div class="donut-chart-card__header">
      <h2 class="donut-chart-card__title">{{ title }}</h2>
      <p class="donut-chart-card__subtitle">{{ subtitle }}</p>
    </div>

    <!-- Estado SIN DATOS -->
    <div v-if="total === 0" class="donut-chart-card__no-data" role="status" aria-live="polite">
      <h3 class="no-data-title">Sin datos en este rango</h3>
      <p class="no-data-text">Ajusta fechas o cambia la línea para ver información.</p>
    </div>

    <!-- Gráfico + etiquetas -->
    <div v-else class="donut-chart-card__content">
      <div class="donut-chart-card__canvas-wrapper">
        <canvas ref="chartRef"></canvas>
      </div>

      <div class="donut-chart-card__badges">
        <div v-for="(badge, index) in badges" :key="index" class="donut-badge">
          <i
            :class="['donut-icon', badge.icon]"
            :style="{ backgroundColor: badge.color }"
          ></i>
          <span>{{ data[index] }} {{ badge.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js'

Chart.register(DoughnutController, ArcElement, Tooltip, Legend)

export default {
  name: 'DonutChart',
  props: {
    title: String,
    subtitle: String,
    labels: Array,
    data: Array,
    total: Number,
    badges: Array
  },
  setup(props) {
    const chartRef = ref(null)
    let chartInstance = null

    const renderChart = () => {
      if (!chartRef.value) return

      const total = props.data.reduce((sum, value) => sum + value, 0)

      chartInstance = new Chart(chartRef.value, {
        type: 'doughnut',
        data: {
          labels: props.labels,
          datasets: [
            {
              data: props.data,
              backgroundColor: props.badges.map(b => b.color), 
              borderColor: '#ffffff',
              borderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                color: '#1e293b',
                font: { family: 'Manrope', size: 13 }
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label
                  const value = context.raw
                  const percentage = ((value / total) * 100).toFixed(1)
                  return `${label}: ${value} (${percentage}%)`
                }
              },
              backgroundColor: '#1e293b',
              titleFont: { family: 'Manrope', size: 13 },
              bodyFont: { family: 'Manrope', size: 12 },
              padding: 10
            }
          }
        }
      })
    }

    onMounted(renderChart)

    // Actualizar donut al cambiar labels o data
    watch(
      () => [ props.labels, props.data ],
      ([newLabels, newData]) => {
        if (!chartInstance) return
        chartInstance.data.labels = newLabels
        chartInstance.data.datasets[0].data = newData
        chartInstance.update()
      },
      { deep: true }
    );

    onBeforeUnmount(() => {
      if (chartInstance) chartInstance.destroy()
    })

    return { chartRef }
  }
}
</script>

<style scoped>
.donut-chart-card {
  background-color: #ffffff;
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.donut-chart-card__header {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.donut-chart-card__title {
  font-family: 'Manrope', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.donut-chart-card__subtitle {
  font-family: 'Manrope', sans-serif;
  font-size: 0.95rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

.donut-chart-card__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
}

.donut-chart-card__canvas-wrapper {
  flex: 1 1 200px;
  min-width: 180px;
  max-width: 300px;
}

canvas {
  width: 100% !important;
  height: 220px !important;
  max-width: 240px;
  margin: 0 auto; 
  display: block;
}

.donut-chart-card__badges {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-family: 'Manrope', sans-serif;
}

.donut-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #1e293b;
}

.donut-icon {
  font-size: 1.2rem;
  padding: 0.25rem;
  border-radius: 0.5rem;
  background-color: #f1f5f9;
}

/* ====== NO DATA ===== */
.donut-chart-card__no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-height: 140px;
  padding: 1.25rem;
  gap: 0.5rem;
  font-family: 'Manrope', sans-serif;
  text-align: center;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border: 2px dashed #e2e8f0;
  border-radius: 1rem;
}

.no-data-title {
  margin: 0;
  font-weight: 700;
  font-size: 1rem;
  color: #0f172a; 
}

.no-data-text {
  margin: 0;
  font-weight: 500;
  font-size: 0.95rem;
  color: #64748b; 
}

</style>
